import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url,
  type = 'website',
  author = 'Dispositif National Nouvelle Chance Tunisie'
}) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'fr';
  
  // Use dynamic domain to support both dnc.tn and www.dnc.tn
  const siteUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}`
    : 'https://www.dnc.tn';
  const defaultImage = '/image/logofr.png';
  
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const metaImage = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : defaultImage;
  
  const defaultTitles = {
    fr: "Dispositif National Nouvelle Chance - Programme de renforcement de l'employabilité et autonomiser les jeunes tunisiens NEET",
    ar: 'المنظومة الوطنية للفرصة الجديدة - منظومة إعادة الإدماج المدرسي في تونس',
    en: 'National New Chance Program - Employability Enhancement and Empowerment Program for Tunisian NEET Youth'
  };
  
  const defaultDescriptions = {
    fr: "Le Dispositif National Nouvelle Chance - Programme de renforcement de l'employabilité et autonomiser les jeunes tunisiens NEET",
    ar: 'توفر المنظومة الوطنية للفرصة الجديدة فرصة ثانية للشباب المنقطعين عن الدراسة في تونس. منظومة إعادة الإدماج المدرسي والمهني للشباب من 18 إلى 30 سنة.',
    en: 'The National New Chance Program - Employability enhancement and empowerment program for Tunisian NEET youth'
  };
  
  const defaultKeywords = {
    fr: 'dnc, DNC, dnc sousse, DNC Sousse, DNNC, dispositif nouvelle chance, dispositif national nouvelle chance, nouvelle chance, DNC Tunisie, réinsertion scolaire, décrochage scolaire, formation professionnelle, jeunes déscolarisés, éducation Tunisie, seconde chance, NEET Tunisie, employabilité jeunes',
    ar: 'dnc, DNC, منظومة الفرصة الجديدة, المنظومة الوطنية للفرصة الجديدة, فرصة جديدة, تونس, سوسة, إعادة الإدماج المدرسي, الانقطاع عن الدراسة, التكوين المهني, الشباب المنقطعين عن الدراسة, التعليم في تونس, قابلية التشغيل',
    en: 'dnc, DNC, dnc sousse, new chance program, national new chance program, Tunisia education, school reintegration, dropout prevention, vocational training, out-of-school youth, NEET Tunisia, employability'
  };
  
  const pageTitle = title || defaultTitles[language];
  const pageDescription = description || defaultDescriptions[language];
  const pageKeywords = keywords || defaultKeywords[language];
  
  return (
    <Helmet>
      <html lang={language} dir={language === 'ar' ? 'rtl' : 'ltr'} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      
      <link rel="canonical" href={fullUrl} />
      
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="Dispositif National Nouvelle Chance" />
      <meta property="og:locale" content={language === 'ar' ? 'ar_TN' : language === 'en' ? 'en_US' : 'fr_FR'} />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="ar_TN" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@dnctunisie" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:image:alt" content={pageTitle} />
      
      <meta name="theme-color" content="#0f4c81" />
      <meta name="msapplication-TileColor" content="#0f4c81" />
      
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "Dispositif National Nouvelle Chance",
          "alternateName": "DNC Tunisie",
          "url": siteUrl,
          "logo": defaultImage,
          "description": pageDescription,
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "TN",
            "addressLocality": "Tunisie"
          },
          "sameAs": [
            "https://www.facebook.com/dnctunisie",
            "https://www.twitter.com/dnctunisie"
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
